"use client";
import { useContextElement } from "@/context/Context";
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

import Image from "next/image";
import Link from "next/link";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ShieldCheck, Warehouse, ChevronDown } from 'lucide-react';
import { viewCart, removeFromCart, beginCheckout } from "@/helpers/gtag";
import { calculateShipping, getTopTwoDepots, getMinOrderViolations } from "@/lib/shippingCalculator";

export default function Cart() {
     const router = useRouter();
	const { cartProducts, setCartProducts } = useContextElement();

     const [cartItems, setCartItems] = useState([]);
     const [totalPrice, setTotalPrice] = useState(0);
     const [shipping, setShipping] = useState(null); // result of calculateShipping
     const [allSuppliers, setAllSuppliers] = useState([]);

     const [isChecked, setIsChecked] = useState(false);
     const [openColets, setOpenColets] = useState({});
     const toggleColet = (i) => setOpenColets((prev) => ({ ...prev, [i]: !prev[i] }));

     // Fetch depot list once so we can build the supplier map
     useEffect(() => {
          fetch("/api/depozite")
               .then((r) => r.json())
               .then((d) => setAllSuppliers((d.depots || []).map((dep) => dep.supplier)))
               .catch(() => {});
     }, []);

     useEffect(() => {
          if (cartProducts && cartProducts.length > 0) {
               const validIds = cartProducts
                    .map(item => item.id)
                    .filter(id => /^\d+$/.test(id));

               if (validIds.length > 0) {
                    fetch(`/api/productsdatabyids?ids=${validIds.join(",")}`)
                         .then((res) => res.json())
                         .then((data) => {
                              const enriched = data.wishlistProducts.map((product) => ({
                                   ...product,
                                   quantity: cartProducts.find(item => item.id == product.id)?.quantity ?? 1,
                              }));
                              setCartItems(enriched);

                              const subtotal = enriched.reduce((sum, product) => {
                                   const price = product.priceReduced && parseFloat(product.priceReduced) < parseFloat(product.price)
                                        ? parseFloat(product.priceReduced)
                                        : parseFloat(product.price);
                                   return sum + price * product.quantity;
                              }, 0);

                              const shippingResult = calculateShipping({
                                   cartItems: enriched,
                                   paymentMethod: "card",
                                   allSuppliers,
                              });
                              setShipping(shippingResult);
                              setTotalPrice(subtotal + shippingResult.totalShipping);

                              viewCart(data.wishlistProducts, cartProducts);
                         })
                         .catch((err) => console.error("Error fetching products:", err));
               } else {
                    setCartItems([]);
                    setTotalPrice(0);
                    setShipping(null);
               }
          } else {
               setCartItems([]);
               setTotalPrice(0);
               setShipping(null);
          }
     }, [cartProducts, allSuppliers]);
     
     
     const setQuantity = (id, quantity) => {
          if (quantity >= 1) {
               const item = cartProducts.filter((elm) => elm.id == id)[0];
               const items = [...cartProducts];
               const itemIndex = items.indexOf(item);
               item.quantity = quantity;
               items[itemIndex] = item;
               setCartProducts(items);
          }
     };
     
     const removeItem = (id) => {
          // GA4: remove_from_cart
          const product = cartItems.find(p => p.id == id);
          const entry = cartProducts.find(p => p.id == id);
          if (product) removeFromCart(product, entry?.quantity || 1);

          setCartProducts((pre) => [...pre.filter((elm) => elm.id != id)]);
     };

     const handleCheckboxChange = (e) => {
          setIsChecked(e.target.checked); 
     };

     const minOrderViolations = shipping ? getMinOrderViolations(shipping.depotLines) : [];

     const handleCheckoutClick = (e) => {
          e.preventDefault();
          if (!isChecked) {
               toast.warning("Trebuie să accepți termenii și condițiile înainte de a continua.");
               return;
          }
          if (minOrderViolations.length > 0) {
               toast.error(
                    minOrderViolations
                         .map((v) => `Din ${v.label} comanda minimă este ${v.minAmount} lei`)
                         .join(". ")
               );
               return;
          }
          // GA4: begin_checkout
          beginCheckout(cartItems, cartProducts);
          router.push("/checkout");
     };

     // Helper function pentru a calcula prețul corect
     const getFinalPrice = (product) => {
          if (product.priceReduced && parseFloat(product.priceReduced) < parseFloat(product.price)) {
               return parseFloat(product.priceReduced);
          }
          return parseFloat(product.price);
     };

     // Helper function pentru a verifica dacă produsul e la reducere
     const isReduced = (product) => {
          return product.priceReduced && parseFloat(product.priceReduced) < parseFloat(product.price);
     };

	return (
		<section className='flat-spacing-11'>
               <style jsx>{`
                    .price-wrapper {
                         display: flex;
                         align-items: center;
                         gap: 8px;
                    }
                    .price-original {
                         text-decoration: line-through;
                         color: #999;
                         font-size: 14px;
                    }
                    .price-reduced {
                         color: #ff4444;
                         font-weight: 600;
                    }
                    .discount-badge {
                         display: inline-block;
                         background: linear-gradient(135deg, #ff4444 0%, #ff6b6b 100%);
                         color: white;
                         padding: 4px 8px;
                         border-radius: 4px;
                         font-size: 11px;
                         font-weight: 600;
                         text-transform: uppercase;
                         letter-spacing: 0.5px;
                         margin-left: 8px;
                         box-shadow: 0 2px 4px rgba(255, 68, 68, 0.3);
                    }
               `}</style>
			<div className='container'>
				<div className='tf-page-cart-wrap'>
					<div className='tf-page-cart-item'>
						<form onSubmit={(e) => e.preventDefault()}>
							<table className='tf-table-page-cart'>
								<thead>
									<tr>
										<th>Produs</th>
										<th>Pret</th>
										<th>Cantitate</th>
										<th>Total</th>
									</tr>
								</thead>
								<tbody>
									{cartItems.map((elm, i) => (
										<tr
											key={i}
											className='tf-cart-item file-delete'
										>
											<td className='tf-cart-item_product'>
												<Link
													href={`/${elm.url}`}
													className='img-box'
												>
													<Image
														alt='img-product'
														src={
															elm.images?.split('|').filter(Boolean)[0]
														}
														width={668}
														height={932}
                                                                      style={{
                                                                           objectFit: "cover",
                                                                      }}
													/>
												</Link>
												<div className='cart-info'>
													<Link
														href={`/${elm.url}`}
														className='cart-title link'
													>
														{elm.name}
                                                                      {isReduced(elm) && (
                                                                           <span className='discount-badge'>
                                                                                🔥 Reducere
                                                                           </span>
                                                                      )}
													</Link>

													<span
														className='remove-cart link remove'
														onClick={() => removeItem(elm.id)}
													>
														Sterge
													</span>
												</div>
											</td>
											<td
												className='tf-cart-item_price'
												cart-data-title='Price'
											>
												<div className='cart-price'>
                                                                 {isReduced(elm) ? (
                                                                      <div className='price-wrapper'>
                                                                           <span className='price-original'>
                                                                                {parseFloat(elm.price).toFixed(2)} RON
                                                                           </span>
                                                                           <span className='price-reduced'>
                                                                                {parseFloat(elm.priceReduced).toFixed(2)} RON
                                                                           </span>
                                                                      </div>
                                                                 ) : (
                                                                      <span>{parseFloat(elm.price).toFixed(2)} RON</span>
                                                                 )}
												</div>
											</td>
											<td
												className='tf-cart-item_quantity'
												cart-data-title='Quantity'
											>
												<div className='cart-quantity'>
													<div className='wg-quantity'>
														<span
															className='btn-quantity minus-btn'
															onClick={() =>
                                                                 setQuantity(
                                                                      elm.id,
                                                                      (cartProducts.find(p => p.id === elm.id)?.quantity || 1) - 1
                                                                 )
                                                            }
														>
															<svg
																className='d-inline-block'
																width={9}
																height={1}
																viewBox='0 0 9 1'
																fill='currentColor'
															>
																<path d='M9 1H5.14286H3.85714H0V1.50201e-05H3.85714L5.14286 0L9 1.50201e-05V1Z' />
															</svg>
														</span>
														<input
															type='text'
															name='number'
															value={
                                                                 cartProducts.find(p => p.id === elm.id)?.quantity || 1
                                                            }
															min={1}
															onChange={(e) =>
                                                                 setQuantity(
                                                                      elm.id,
                                                                      parseInt(e.target.value) || 1
                                                                 )
                                                            }
														/>
														<span
															className='btn-quantity plus-btn'
															onClick={() => setQuantity(
                                                                 elm.id,
                                                                 (cartProducts.find(p => p.id === elm.id)?.quantity || 0) + 1
                                                            )}
														>
															<svg
																className='d-inline-block'
																width={9}
																height={9}
																viewBox='0 0 9 9'
																fill='currentColor'
															>
																<path d='M9 5.14286H5.14286V9H3.85714V5.14286H0V3.85714H3.85714V0H5.14286V3.85714H9V5.14286Z' />
															</svg>
														</span>
													</div>
												</div>
											</td>
											<td
												className='tf-cart-item_total'
												cart-data-title='Total'
											>
												<div
													className='cart-total'
													style={{
														minWidth: "60px",
													}}
												>
                                                            {(getFinalPrice(elm) * (cartProducts.find(p => p.id === elm.id)?.quantity || 1)).toFixed(2)} RON
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
							{!cartProducts.length && (
								<>
									<div className='row align-items-center mb-5'>
										<div className='col-6 fs-18'>
                                                       Coșul tău de cumpărături este gol. 
										</div>
										<div className='col-6'>
											<Link
												href={`/shop-default`}
												className='tf-btn btn-fill animate-hover-btn radius-3 w-100 justify-content-center'
												style={{
													width: "fit-content",
												}}
											>
												Vezi produse
											</Link>
										</div>
									</div>
								</>
							)}
							
						</form>
					</div>
					<div className='tf-page-cart-footer'>
						<div className='tf-cart-footer-inner'>

							{/* Smart guidance banner */}
							{shipping && (() => {
								const top2 = getTopTwoDepots(shipping.depotLines);
								if (shipping.depotCount > 2) {
									return (
										<div className="tf-cart-totals-discounts" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4, paddingBottom: 10, marginBottom: 4 }}>
											<h3 style={{ fontSize: 13, color: "#92400e", margin: 0 }}>
												⚠️ Transport pentru {shipping.depotCount} colete separate
											</h3>
											<p style={{ fontSize: 12, color: "#555", margin: 0, lineHeight: 1.5 }}>
												{shipping.orderSubtotal > 500 ? (
													<>Pentru transport gratuit, păstrează produse din maxim 2 depozite — cele mai avantajoase acum sunt{" "}
														{top2.map((d, i) => (
															<span key={i}>
																<Link href={`/depozite/${d.slug}`} style={{ color: "#b45309", fontWeight: 600, textDecoration: "underline" }}>
																	{d.label}
																</Link>
																{i < top2.length - 1 ? " și " : ""}
															</span>
														))}.
													</>
												) : (
													<>Produsele tale provin din depozite diferite, fiecare depozit are transport separat. Adaugă mai multe produse dintr-un singur depozit pentru a beneficia de transport gratuit la comenzi de peste 500 RON.</>
												)}
											</p>
										</div>
									);
								}
								if (shipping.isFreeShipping) {
									return (
										<div className="tf-cart-totals-discounts" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4, paddingBottom: 10, marginBottom: 4 }}>
											<p style={{ fontSize: 12, color: "#166534", margin: 0, fontWeight: 600 }}>
												🎉 Ai transport gratuit!
											</p>
										</div>
									);
								}
								// 1–2 depots, under 500 RON → show hint
								return (
									<div className="tf-cart-totals-discounts" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4, paddingBottom: 10, marginBottom: 4 }}>
										<p style={{ fontSize: 12, color: "#333", margin: 0, lineHeight: 1.5 }}>
											Produsele tale provin din depozite diferite, fiecare depozit are transport separat. Adaugă mai multe produse dintr-un singur depozit pentru a beneficia de transport gratuit la comenzi de peste 500 RON.
										</p>
									</div>
								);
							})()}

							<div className='tf-page-cart-checkout'>

								{/* Minimum order per depot warnings */}
								{minOrderViolations.length > 0 && (
									<div className="tf-cart-totals-discounts" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4, paddingBottom: 10, marginBottom: 4 }}>
										{minOrderViolations.map((v, i) => (
											<p key={i} style={{ fontSize: 12, color: "#b91c1c", margin: 0, fontWeight: 600 }}>
												⚠️ Din {v.label} comanda minimă este {v.minAmount} lei
											</p>
										))}
									</div>
								)}

								{/* Per-depot shipping lines */}
								{shipping && (
									<div style={{ marginBottom: 8 }}>
										{shipping.depotLines.map((line, i) => {
											const products = cartItems.filter((p) => p.supplier?.trim() === line.supplier);
											const isOpen = !!openColets[i];
											return (
												<div key={i} style={{ marginBottom: 4, borderBottom: "1px solid #eee" }}>
													<button
														onClick={() => toggleColet(i)}
														style={{
															width: "100%", background: "none", border: "none",
															display: "flex", alignItems: "center", justifyContent: "space-between",
															padding: "6px 0", cursor: "pointer",
														}}
													>
														<span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
															<Warehouse size={15} style={{ opacity: 0.5, flexShrink: 0 }} />
															Colet {i + 1}
															<span style={{ fontWeight: 400, color: "#888", fontSize: 12 }}>
																· {line.qty} {line.qty === 1 ? "produs" : "produse"}
															</span>
														</span>
														<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
															{line.cost === 0 ? (
																<span style={{ color: "#1a7f3c", fontWeight: 700, fontSize: 14 }}>Gratuit</span>
															) : (
																<span style={{ fontWeight: 600, fontSize: 14 }}>{line.cost} RON</span>
															)}
															<ChevronDown size={14} style={{ opacity: 0.5, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
														</span>
													</button>
													{isOpen && (
														<div style={{ borderTop: "1px solid #f0f0f0", padding: "6px 0 8px 0" }}>
															<p style={{ fontSize: 11, color: "#aaa", margin: "0 0 6px 0" }}>
																{line.label}
															</p>
															{products.map((p, j) => {
																const price = p.priceReduced && parseFloat(p.priceReduced) < parseFloat(p.price)
																	? parseFloat(p.priceReduced)
																	: parseFloat(p.price);
																return (
																	<div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, padding: "5px 0", borderBottom: j < products.length - 1 ? "1px solid #f0f0f0" : "none" }}>
																		<span style={{ color: "#333", flex: 1, fontSize: 12, lineHeight: 1.4 }}>
																			{p.name}
																			<span style={{ display: "block", color: "#999", fontSize: 11 }}>{p.quantity} {p.quantity === 1 ? "bucată" : "bucăți"}</span>
																		</span>
																		<span style={{ fontWeight: 500, fontSize: 12, whiteSpace: "nowrap" }}>{(price * p.quantity).toFixed(2)} RON</span>
																	</div>
																);
															})}
															<div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 6, paddingTop: 6, borderTop: "1px solid #e8e8e8", color: "#555" }}>
																<span>Transport colet</span>
																{line.cost === 0 ? (
																	<span style={{ color: "#1a7f3c", fontWeight: 600 }}>Gratuit</span>
																) : (
																	<span style={{ fontWeight: 600 }}>{line.cost} RON</span>
																)}
															</div>
														</div>
													)}
												</div>
											);
										})}
										{shipping.totalShipping > 0 && shipping.depotLines.length > 1 && (
											<div className='tf-cart-totals-discounts' style={{ borderTop: "1px solid #eee", paddingTop: 6, marginTop: 4 }}>
												<h3 style={{ fontSize: 14, fontWeight: 700 }}>Total livrare</h3>
												<span className='total-value' style={{ fontWeight: 700 }}>{shipping.totalShipping} RON</span>
											</div>
										)}
									</div>
								)}

								<div className='tf-cart-totals-discounts'>
									<h3>Subtotal</h3>
									<span className='total-value'>
										{totalPrice.toFixed(2)} RON<br/>
                                                  <small style={{fontSize: 10}}>TVA inclus(21%)</small>
									</span>
                                             
								</div>
				
								<div className='cart-checkbox'>
									<input
										type='checkbox'
										className='tf-check'
										id='check-agree'
                                                  checked={isChecked}
                                                  onChange={handleCheckboxChange}
									/>
									<label
										htmlFor='check-agree'
										className='fw-4'
									>
										Sunt de acord cu <Link href={`/termeni-si-conditii`}>termenii si conditiile</Link>
									</label>
								</div>
								<div className='cart-checkout-btn'>
									<Link
										href={`/checkout`}
										className='tf-btn w-100 btn-fill animate-hover-btn radius-3 justify-content-center'
                                                  onClick={handleCheckoutClick}
									>
										<span>Către plată</span>
									</Link>
								</div>
								<div className='tf-page-cart_imgtrust'>
									<p className='text-center fw-6'>
                                                  <ShieldCheck /> Plată securizată garantată prin <Link href="https://stripe.com/">Stripe</Link>
									</p>
									<div className='cart-list-social'>
										<div className='payment-item'>
											<svg
												viewBox='0 0 38 24'
												xmlns='http://www.w3.org/2000/svg'
												role='img'
												width={38}
												height={24}
												aria-labelledby='pi-visa'
											>
												<title id='pi-visa'>
													Visa
												</title>
												<path
													opacity='.07'
													d='M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z'
												/>
												<path
													fill='#fff'
													d='M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32'
												/>
												<path
													d='M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z'
													fill='#142688'
												/>
											</svg>
										</div>
							
										<div className='payment-item'>
											<svg
												viewBox='0 0 38 24'
												xmlns='http://www.w3.org/2000/svg'
												role='img'
												width={38}
												height={24}
												aria-labelledby='pi-master'
											>
												<title id='pi-master'>
													Mastercard
												</title>
												<path
													opacity='.07'
													d='M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z'
												/>
												<path
													fill='#fff'
													d='M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32'
												/>
												<circle
													fill='#EB001B'
													cx={15}
													cy={12}
													r={7}
												/>
												<circle
													fill='#F79E1B'
													cx={23}
													cy={12}
													r={7}
												/>
												<path
													fill='#FF5F00'
													d='M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z'
												/>
											</svg>
										</div>
						
									
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}